import { createFileRoute, Link } from '@tanstack/react-router'
import { GuideArticle, GuideSection } from '~/components/guide/GuideArticle'

export const Route = createFileRoute('/guide/dispatch')({
  component: DispatchGuide,
})

function DispatchGuide() {
  return (
    <GuideArticle>
      <GuideSection title="First Response dispatch">
        <p>
          The <Link to="/" className="underline underline-offset-2">home page</Link>{' '}
          is the dispatch view. Enter a <strong>job number</strong> and press{' '}
          <strong>Search</strong>. The job is resolved to its property (asset) —
          an unresolvable job number is rejected with an error — and you then see
          the nearest available officers and the live jobs around that property,
          plotted on a Google map — switch it to <strong>Satellite</strong>, or
          drag the Street View figure onto a street to see it at ground level.
          When a lookup finds
          nothing, a notification shows the message reported by the lookup
          (e.g. <em>No officers found</em> or <em>No jobs nearby found</em>).
        </p>
      </GuideSection>

      <GuideSection title="Linking to a job">
        <p>
          Open <code>/?job=J123455</code> to skip the search: the job is
          resolved and a dispatch session starts straight away, exactly as if
          you had pressed <strong>Search</strong>. Each visit to the link
          starts a new session. Use it to link from another tool such as an
          Argus dashboard. If you aren&apos;t signed in yet, the session starts
          once you have signed in.
        </p>
      </GuideSection>

      <GuideSection title="Officers">
        <p>
          Officers are ranked by driving time to the property (fastest first),
          each showing their guarding partner, phone, road distance, and when
          they were last seen. Each officer is numbered on an orange marker —
          on the map and in the list. Selecting an officer card focuses its
          map marker, and vice-versa. The external-link button on a card opens
          the partner&apos;s contacts tab in Chase2Base.
        </p>
      </GuideSection>

      <GuideSection title="Jobs nearby">
        <p>
          The <strong>Search area</strong> controls set the radius (miles) and time
          window (minutes) used to find jobs near the property. Jobs list their
          service, execution status, owning partner, allocated officer (or{' '}
          <em>No officer allocated</em>), distance and dates. Job markers are
          blue (brighter when in progress) to distinguish them from officers.
          The external-link button on a job card (or its map popup) opens the
          job in Chase2Base. A key in the bottom-left corner of the map
          explains the marker icons and colours.
        </p>
      </GuideSection>

      <GuideSection title="Shareable links">
        <p>
          Searching for a job creates a persistent <em>dispatch session</em>{' '}
          with its own URL — <code>/dispatch/&lt;id&gt;</code>. Anyone with the{' '}
          <code>dispatch:read</code> permission can open that link and see the
          same operatives, jobs, and recorded outcomes. The radius and interval
          filters are mirrored into the query string{' '}
          (<code>?radius=&amp;interval=</code>), so a shared link reproduces the
          exact view used when the session was created.
        </p>
        <p>
          The first search stores its operatives and jobs results against the
          session, so opening or sharing the link replays that stored snapshot
          from the database instead of re-querying EGIS and Google Routes. Use
          the <strong>Refresh</strong> button to pull live data on demand — it
          re-runs the lookups against EGIS (driving times via Google Routes) and
          updates the stored snapshot.
        </p>
      </GuideSection>

      <GuideSection title="Recording contact">
        <p>
          While viewing a session, users with <code>dispatch:write</code> can
          record what happened with each officer and job:
        </p>
        <p>
          Officers and jobs share the same controls, and each is tracked
          independently:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Called</strong> — the checkbox records a call/contact
            attempt against that officer or job. Ticked it by mistake? Un-tick
            it to clear the attempt.
          </li>
          <li>
            <strong>Accepted</strong> — marks the officer or job as having
            accepted. An officer is recorded against the job number that was
            searched, so there is nothing to type; a job uses its own number.
            Acceptance supersedes a call attempt and is not downgraded back to a
            call, but it can be undone (see below).
          </li>
          <li>
            <strong>Declined</strong> — records that the officer or job was
            declined. A declined entry can still be accepted later.
          </li>
          <li>
            <strong>Undo</strong> — clicked Accepted or Declined by mistake? The{' '}
            <strong>Undo</strong> button next to the recorded outcome clears it
            back to no outcome, so the Accepted/Declined controls return. The
            clear is itself logged, so the audit trail keeps both the mistake
            and the undo.
          </li>
        </ul>
        <p>
          Every recorded outcome shows the name of the user who recorded it so
          there is a clear audit trail.
        </p>
      </GuideSection>

      <GuideSection title="Permissions">
        <p>
          Access to dispatch is controlled by two permissions:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <code>dispatch:read</code> — view sessions, open shared links, and
            run operative/job lookups.
          </li>
          <li>
            <code>dispatch:write</code> — create new sessions, refresh live
            data, and record outcomes (call attempts, acceptances, declines).
          </li>
        </ul>
        <p>
          The seeded roles grant these as follows: <strong>Viewer</strong> has{' '}
          <code>dispatch:read</code>; <strong>Editor</strong> has{' '}
          <code>dispatch:read</code> and <code>dispatch:write</code>;{' '}
          <strong>Admin</strong> implies all permissions.
        </p>
      </GuideSection>

      <GuideSection title="API and reporting">
        <p>
          Operative and job lookups made from within a dispatch session are
          logged against it, recording the caller, timestamp, result count, and
          any errors. (Direct REST calls to the operatives/jobs endpoints are
          logged without a session association.) The full session — parameters,
          outcomes, and lookup log — is available over REST:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <code>GET /api/v1/dispatch/sessions</code> — list sessions
            (requires <code>dispatch:read</code>).
          </li>
          <li>
            <code>GET /api/v1/dispatch/sessions/&#123;id&#125;</code> — retrieve a
            single session with its outcomes and lookup history.
          </li>
          <li>
            <code>PUT /api/v1/dispatch/sessions/&#123;id&#125;/outcomes</code> — record
            or update an outcome (requires <code>dispatch:write</code>).
          </li>
          <li>
            <code>DELETE /api/v1/dispatch/sessions/&#123;id&#125;/outcomes</code> — clear
            a recorded outcome for an officer or job (requires{' '}
            <code>dispatch:write</code>).
          </li>
          <li>
            <code>GET /api/v1/dispatch/property?jobNumber=…</code> resolves a job
            to its property; <code>GET /api/v1/dispatch/operatives?jobNumber=…</code>{' '}
            and{' '}
            <code>GET /api/v1/dispatch/jobs?jobNumber=…&amp;radius=…&amp;interval=…</code>{' '}
            are the underlying lookups, each requiring <code>dispatch:read</code>.
          </li>
        </ul>
        <p>
          MCP tools are generated automatically from these routes. See the{' '}
          <Link to="/openapi" className="underline underline-offset-2">API reference</Link>{' '}
          and{' '}
          <Link to="/mcp-instructions" className="underline underline-offset-2">MCP instructions</Link>{' '}
          for details.
        </p>
      </GuideSection>
    </GuideArticle>
  )
}
